import React from "react";
import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MODULES } from "@/data/course";
import { CheckCircle2, Lock, Clock, PlayCircle } from "lucide-react";

export default function Course() {
  const { user, progress } = useAuth();
  const completed = progress?.completed_modules || [];
  const isPro = user?.plan === "pro";
  const freeMods = user?.free_modules || [];

  const hasAccess = (id) => isPro || freeMods.includes(id);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Trading Course</h1>
        <p className="text-slate-400 mt-1">15 modules · progress at your own pace · earn XP as you go.</p>
      </div>

      {progress && (
        <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-300 font-medium">Course progress</span>
            <span className="font-mono text-blue-400 font-semibold">{progress.course_percent}%</span>
          </div>
          <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all" style={{ width: `${progress.course_percent}%` }} />
          </div>
          <div className="text-xs text-slate-500 mt-2 font-mono">{completed.length} of 15 modules completed</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.map((m) => {
          const Icon = Lucide[m.icon] || Lucide.BookOpen;
          const done = completed.includes(m.id);
          const access = hasAccess(m.id);
          return (
            <div key={m.id} data-testid={`module-card-${m.id}`}
              className={`relative bg-[#0F172A] border rounded-2xl p-5 flex flex-col transition-all ${access ? "border-slate-800 hover:border-blue-500/50" : "border-slate-800/60"}`}>
              {!access && (
                <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-300 bg-blue-500/15 border border-blue-500/30 rounded-full px-2 py-0.5">
                  <Lock className="w-3 h-3" /> Pro
                </span>
              )}
              <div className="flex items-start justify-between mb-3">
                <div className={`w-11 h-11 rounded-xl grid place-items-center ${done ? "bg-emerald-500/15 border border-emerald-500/25" : "bg-blue-500/15 border border-blue-500/20"}`}>
                  <Icon className={`w-5 h-5 ${done ? "text-emerald-300" : "text-blue-300"}`} />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-800/60 rounded px-2 py-1">{m.stage}</span>
              </div>
              <div className="text-xs font-mono text-slate-500 mb-1">Module {m.id}</div>
              <h3 className="font-display text-lg font-semibold leading-snug">{m.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed flex-1">{m.summary}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                <span className="flex items-center gap-1.5 text-xs text-slate-500 font-mono"><Clock className="w-3.5 h-3.5" />{m.minutes} min</span>
                {!access ? (
                  <Link to={`/course/${m.id}`} data-testid={`module-locked-${m.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300">
                    <Lock className="w-3.5 h-3.5" /> Unlock with Pro
                  </Link>
                ) : done ? (
                  <Link to={`/course/${m.id}`} data-testid={`module-review-${m.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4" /> Review
                  </Link>
                ) : (
                  <Link to={`/course/${m.id}`} data-testid={`module-start-${m.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300">
                    <PlayCircle className="w-4 h-4" /> Start
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
